<template>
  <main class="min-h-screen bg-gray-300">
    <sqr-progress v-show="loading" />
    <sqr-error-banner :error="loadError" />

    <section
      v-if="!loading && invoiceId && invoice && invoice.status == 'draft'"
      class="bg-gray-600 sm:py-8"
    >
      <div class="max-w-4xl mx-auto sm:p-4">
        <div class="bg-white lg:rounded lg:shadow">
          <invoice-header-form
            class="p-2 lg:p-4"
            :aid="aid"
            :invoice-id="invoiceId"
            :invoice="invoice"
            @change="fieldSet"
            :validations="$v.invoice"
          />
          <invoice-form
            class="p-2 lg:p-4 pb-8 border-t"
            :invoice="invoice"
            @change="fieldSet"
            @number-new="numberNew($event)"
            :validations="$v"
          />
          <invoice-sessions-form
            :invoice="invoice"
            :no-taxes="noTaxes"
            @add="sessionAdd($event)"
            @change="sessionChange($event)"
            @remove="sessionRemove($event)"
            @line-add="lineAdd($event)"
            @line-change="lineChange($event)"
            @line-remove="lineRemove($event)"
            @sort="sessionSort($event)"
            :v="$v.invoice.sessions"
          />
          <invoice-totals-form
            class=" bg-gray-50"
            :invoice="invoice"
            :no-taxes="noTaxes"
            @invoice-change="fieldSet($event)"
          />
        </div>
      </div>
    </section>
    <section class="section" v-if="adBlockIssue">
      <div class="container">
        <div class="columns is-centered">
          <div class="column is-12-mobile is-9-tablet is-7-desktop">
            <div class="notification is-warning">
              {{ $t('invoice_edit_ad_block_issue') }}
            </div>
          </div>
        </div>
      </div>
    </section>
    <section
      class="sticky bottom-0 bg-white py-2 border-t border-b z-20"
      v-if="!loading && invoice && invoice.status === 'draft'"
    >
      <div class="max-w-5xl mx-auto">
        <div class="flex justify-end space-x-2 items-center">
          <sqr-input-checkbox
            class="hidden lg:block"
            v-if="account"
            :placeholder="$t('invoice_preview_auto')"
            :value="account.preview"
            @change="accountFieldSet({ field: 'preview', value: $event })"
          />
          <sqr-button
            class="hidden lg:block"
            :label="$t('invoice_preview')"
            color="white"
            size="medium"
            @click="preview()"
            :is-loading="previewing"
          >
            <template slot="icon">
              <DocumentTextIcon class="inline w-4 h-4 stroke-current" />
            </template>
          </sqr-button>
          <sqr-button
            class="hidden lg:block"
            :label="$t('invoice_print')"
            color="white"
            @click="print()"
            :is-loading="previewing"
          >
            <template slot="icon">
              <PrinterIcon class="inline w-4 h-4 stroke-current" />
            </template>
          </sqr-button>

          <sqr-button
            class="hidden lg:block"
            :label="$t('invoice_download')"
            color="white"
            @click="download()"
            :is-loading="previewing"
          >
            <template slot="icon">
              <DocumentDownloadIcon class="inline w-4 h-4 stroke-current" />
            </template>
          </sqr-button>
          <sqr-button
            :label="$t('buttons_cancel')"
            color="white"
            @click="cancel()"
          />
          <sqr-button
            :label="$t('invoice_save')"
            color="white"
            @click="saveAndGo()"
            :disabled="validating"
            :loading="saving"
          />
          <sqr-button
            :label="$t('invoice_validate')"
            color="pink"
            size="white"
            @click="validateAndGo()"
            :disabled="saving"
            :loading="validating"
          />
        </div>
      </div>
    </section>
    <section class="section" v-if="account && account.preview">
      <div class="container">
        <invoice-preview :invoice="invoice" />
      </div>
    </section>
  </main>
</template>

<script>
// whitelist: bg-pink-600 bg-pink-500 shadow-outline-pink bg-pink-600
import { mapGetters, mapMutations, mapActions } from 'vuex';

import { validationMixin } from 'vuelidate';
import { required, requiredIf } from 'vuelidate/lib/validators';
import emission from '../validations/emission';

import SqrInputCheckbox from '@/components/SqrInputCheckbox';
import SqrProgress from '@/components/SqrProgress';
import SqrErrorBanner from '@/components/SqrErrorBanner';
import SqrButton from '@/components/SqrButton';

import InvoiceHeaderForm from '../forms/InvoiceHeaderForm';
import InvoiceForm from '../forms/InvoiceForm';
import InvoiceSessionsForm from '../forms/InvoiceSessionsForm';
import InvoiceTotalsForm from '../forms/InvoiceTotalsForm';

import practiceVal from '../validations/practice';
import patientVal from '../validations/patient';

import invoiceRender from '../pdfs/invoice';
import {
  DocumentDownloadIcon,
  DocumentTextIcon,
  PrinterIcon
} from '@vue-hero-icons/outline';
const pdfMake = require('pdfmake/build/pdfmake.js');
const pdfFonts = require('../pdfs/vfs_fonts.js');
pdfMake.fonts = {
  Helvetica: {
    normal: 'Helvetica-Regular.ttf',
    bold: 'Helvetica-Bold.ttf'
  }
};
pdfMake.vfs = pdfFonts.pdfMake.vfs;

// import RenderWorker from '../pdfs/render3.worker.js';
// const render = new RenderWorker();

export default {
  name: 'InvoiceEdit',
  mixins: [validationMixin],
  components: {
    SqrProgress,
    SqrErrorBanner,
    InvoiceHeaderForm,
    InvoiceSessionsForm,
    InvoiceForm,
    InvoiceTotalsForm,
    InvoicePreview: () =>
      import(
        /* webpackChunkName: "InvoicePreview" */ '../components/InvoicePreview'
      ),
    SqrInputCheckbox,
    SqrButton,
    DocumentTextIcon,
    PrinterIcon,
    DocumentDownloadIcon
  },
  props: {
    aid: String,
    id: String
  },
  computed: {
    ...mapGetters('auth', ['uid']),
    ...mapGetters('account', ['accountId', 'account']),
    ...mapGetters('invoice', [
      'invoiceId',
      'invoice',
      'loading',
      'loadError',
      'saving',
      'saveError',
      'validating',
      'validateError',
      'error'
    ]),
    noTaxes() {
      return (
        this.invoice &&
        this.invoice.issuer &&
        !this.invoice.issuer.vatNumber &&
        this.invoice.lines &&
        this.invoice.lines.every(l => !l.vatRate)
      );
    }
  },
  data() {
    return {
      previewing: false,
      skipSave: false,
      adBlockIssue: false
    };
  },
  validations() {
    return {
      invoice: {
        issuer: practiceVal,
        provider: this.invoice.noProvider ? {} : practiceVal,
        patient: patientVal,
        sessions: {
          $each: {
            date: { required },
            lines: {
              $each: {
                tarif: { required },
                code: { requiredIf: requiredIf(m => m.tarif === '590') },
                label: {},
                description: {},
                quantity: { required },
                unitPrice: { required },
                vatRate: { required }
              }
            }
          }
        },
        date: { required, emission }, // TODO date must be after treatment dates
        number: { required },
        reason: { required }
      }
    };
  },
  async mounted() {
    const accountId = this.accountId;
    const id = this.$route.params.id;
    this.$a().logEvent('invoice_edit');
    const invoice = await this.subscribe({ accountId, id });
    if (invoice && invoice.status === 'valid') {
      this.$router.replace({
        name: 'invoice-view',
        params: this.$route.params
      });
    }
  },
  async beforeRouteLeave(to, from, next) {
    if (this.skipSave) {
      return next();
    } else {
      const accountId = this.accountId;
      return this.save({ accountId }).finally(() => next());
    }
  },
  watch: {
    'invoice.status': function(n) {
      if (n === 'valid') {
        this.$router.replace({
          name: 'invoice-view',
          params: this.$route.params
        });
      }
    }
  },
  methods: {
    ...mapMutations('invoice', ['fieldSet', 'numberNew']),
    ...mapMutations('invoice', [
      'lineAdd',
      'lineChange',
      'lineRemove',
      'sessionSort',
      'sessionAdd',
      'sessionChange',
      'sessionRemove'
    ]),
    ...mapActions('invoice', ['subscribe', 'save', 'validate']),
    ...mapActions('account', { accountFieldSet: 'fieldSet' }),
    cancel() {
      this.skipSave = true;
      this.$router.back();
    },
    saveAndGo() {
      this.save({ accountId: this.accountId }).then(() => {
        this.skipSave = true;
        this.$router.back();
      });
    },
    async validateAndGo() {
      if (this.$v.$invalid) {
        this.$v.$touch();
        alert(this.$t('invoice_validate_invalid'));
        return;
      } else if (confirm(this.$t('invoice_validate_confirm'))) {
        const accountId = this.accountId;
        const id = await this.validate({ accountId });
        this.skipSave = true;
        this.$route.replace({ name: 'invoice-view', params: { id } });
      }
    },
    async preview() {
      const model = await invoiceRender(
        this.invoice,
        this,
        this.$i18n && this.$i18n.current,
        this.uid
      );
      const pdf = pdfMake.createPdf(model.doc, model.tableLayouts);
      const child = window.open('', '');
      pdf.open({}, child);
      setTimeout(() => {
        this.adBlockIssue = child.closed;
      }, 1000);
    },
    async print() {
      const model = await invoiceRender(
        this.invoice,
        this,
        this.$i18n && this.$i18n.current,
        this.uid
      );
      const pdf = pdfMake.createPdf(model.doc, model.tableLayouts);
      const child = window.open('', '');
      pdf.print({}, child);
      setTimeout(() => {
        this.adBlockIssue = child.closed;
      }, 1000);
    },
    async download() {
      const model = await invoiceRender(
        this.invoice,
        this,
        this.$i18n && this.$i18n.current,
        this.uid
      );
      const pdf = pdfMake.createPdf(model.doc, model.tableLayouts);
      pdf.download(this.invoice.number + '.pdf');
      // render.onmessage = event => {
      //   console.log('message', event);
      // };
      // render.postMessage({
      //   invoice: this.invoice,
      //   locale: this.$i18n.current,
      //   uid: this.uid
      // });
    }
  },
  metaInfo() {
    return {
      title: this.$t('invoice_edit_title_alt', this.invoice)
    };
  }
};
</script>
